/**
 * IoT Vendor Configuration
 * 
 * This file contains the vendor and client IDs for all IoT integrations
 * with the Kapitani Middleware platform.
 * 
 * IDs are assigned by Kapitani when vendors/clients are registered.
 * These are fixed values and should only be updated if Kapitani reassigns them.
 * 
 * @see https://middleware.kapitani.co/vendor - Vendor Management
 * @see https://middleware.kapitani.co/client - Client Management
 */

// Kapitani API Base URL (Production: middleware.kapitani.co)
// .trim(): the Vercel env value has a trailing newline
export const KAPITANI_BASE_URL = (process.env.REACT_APP_KAPITANI_BASE_URL || '').trim() ||
  'https://middleware.kapitani.co/api';

/**
 * IoT Vendor Definitions
 * 
 * Each vendor has:
 * - vendorId: Assigned by Kapitani when vendor is created
 * - clientId: Assigned by Kapitani when client (Smart Farm Platform) subscribes to vendor services
 * - name: Display name
 * - description: What the vendor provides
 * - services: Available API endpoints
 */
export const IOT_VENDORS = {
  // ============================================
  // AVIALITE - LED Management System
  // ============================================
  AVIALITE: {
    vendorId: '7376882806649135104',
    clientId: '7376891376107134976',
    name: 'Avialite-SmartFarm',
    description: 'LED Management System for Smart Farm - IoT lighting control and monitoring',
    icon: 'light-bulb',
    color: '#FCD34D', // Yellow
    services: {
      register: {
        endpoint: '/register',
        method: 'POST',
        description: 'Register new LED device'
      },
      list: {
        endpoint: '/device-list',
        method: 'GET',
        description: 'List all LED devices'
      },
      edit: {
        endpoint: '/edit',
        method: 'PATCH',
        description: 'Update LED device info'
      },
      delete: {
        endpoint: '/delete',
        method: 'DELETE',
        description: 'Remove LED device'
      },
      control: {
        endpoint: '/control-led',
        method: 'POST',
        description: 'Control LED (ON/OFF, brightness)',
        status: 'disabled', // MQTT backend issue
        note: 'Waiting for Kapitani to fix MQTT backend (502 error)'
      }
    }
  },

  // ============================================
  // HAVVA - AI Camera Analysis System
  // ============================================
  HAVVA: {
    // Smart Farm HAVVA credentials from middleware.kapitani.co
    vendorId: '7376882196465983488',
    clientId: '7376891376107134976',
    name: 'Smart Farm HAVVA',
    description: 'HAVVA Camera Management System for Smart Farm - AI-powered plant health analysis',
    icon: 'camera',
    color: '#34D399', // Green
    services: {
      register: {
        endpoint: '/register',
        method: 'POST',
        description: 'Register new camera'
      },
      list: {
        endpoint: '/device-list',
        method: 'GET',
        description: 'List all cameras'
      },
      uploadImage: {
        endpoint: '/upload-image',
        method: 'POST',
        description: 'Upload image for analysis',
        useAlternativeCredentials: true // Uses Test Innates credentials
      },
      history: {
        endpoint: '/history',
        method: 'GET',
        description: 'Get analysis history'
      }
    }
  },

  // ============================================
  // ELLIANCE - Environmental Sensors
  // ============================================
  ELLIANCE: {
    // Elliance credentials from middleware.kapitani.co
    vendorId: '7376884591598116864',
    clientId: '7376891376107134976',
    name: 'Elliance-SmartFarm',
    description: 'Environmental Sensor Management System for Smart Farm - Temperature, humidity, pH, and soil moisture monitoring',
    icon: 'chart-bar',
    color: '#60A5FA', // Blue
    status: 'active', // Configured on 2026-01-08
    // API Documentation: Based on IE-Middleware-Elliance
    services: {
      // API 1: List of Sensors
      list: {
        endpoint: '/device-list',
        method: 'GET',
        description: 'Get list of Elliance sensors from middleware database',
        query: {
          farmerId: 'string',
          farmId: 'string',
          vendorId: 'string'
        },
        response: {
          sensors: [{ id: 'number', name: 'string' }]
        }
      },
      // API 2: Sensor Status
      status: {
        endpoint: '/device-status',
        method: 'GET',
        description: 'Get Elliance sensor status (online/offline)',
        query: {
          farmerId: 'string',
          farmId: 'string',
          vendorId: 'string'
        },
        response: {
          sensors: [{ id: 'number', name: 'string', status: 'online|offline' }]
        }
      },
      // API 3: Sensor Data
      data: {
        endpoint: '/sensor-data',
        method: 'GET',
        description: 'Get sensor readings with date range filter',
        query: {
          farmerId: 'string',
          farmId: 'string',
          vendorId: 'string',
          id: 'number', // Sensor ID
          startDate: 'string', // YYYY-MM-DD HH:mm:ss
          endDate: 'string'    // YYYY-MM-DD HH:mm:ss
        },
        response: {
          Water_pH: [{ value: 'number', timestamp: 'string' }]
        }
      }
    }
  }
};

/**
 * Get vendor configuration by type
 * @param {string} vendorType - 'AVIALITE' | 'HAVVA' | 'ELLIANCE'
 * @returns {Object} Vendor configuration
 */
export const getVendorConfig = (vendorType) => {
  const vendor = IOT_VENDORS[vendorType?.toUpperCase()];
  if (!vendor) {
    console.warn(`[IoT Config] Unknown vendor type: ${vendorType}`);
    return null;
  }
  return vendor;
};

/**
 * Get API URL for a vendor service
 * @param {string} vendorType - 'AVIALITE' | 'HAVVA' | 'ELLIANCE'
 * @param {string} serviceName - Service name (e.g., 'register', 'list')
 * @returns {string} Full API URL
 */
export const getServiceUrl = (vendorType, serviceName) => {
  const vendor = getVendorConfig(vendorType);
  if (!vendor || !vendor.services[serviceName]) {
    console.error(`[IoT Config] Service not found: ${vendorType}/${serviceName}`);
    return null;
  }
  
  const service = vendor.services[serviceName];
  return `${KAPITANI_BASE_URL}/client/${vendor.clientId}${service.endpoint}`;
};

/**
 * Check if vendor is active (has valid IDs)
 * @param {string} vendorType - Vendor type
 * @returns {boolean} True if vendor is configured
 */
export const isVendorActive = (vendorType) => {
  const vendor = getVendorConfig(vendorType);
  return vendor && vendor.vendorId && vendor.clientId && vendor.status !== 'pending';
};

/**
 * Get all active vendors
 * @returns {Array} Array of active vendor configs
 */
export const getActiveVendors = () => {
  return Object.entries(IOT_VENDORS)
    .filter(([key, vendor]) => isVendorActive(key))
    .map(([key, vendor]) => ({ key, ...vendor }));
};

// Default export for convenience
export default IOT_VENDORS;
